import React, { useState, useEffect, useRef } from 'react';
import { 
  QrCode, 
  Download, 
  Copy, 
  Check, 
  Plus,
  Trash2,
  Edit2,
  Percent,
  Euro,
  Tag,
  FileText,
  Printer,
  RefreshCw,
  Megaphone,
  Eye,
  EyeOff,
  Palette,
  Phone,
  MapPin,
  Loader2,
  X,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { marketingAPI } from '../lib/api';
import { toast } from 'sonner';

export const MarketingPage = () => {
  const { restaurant } = useAuth();
  const [loading, setLoading] = useState(true);
  const [qrCode, setQrCode] = useState(null);
  const [webshopUrl, setWebshopUrl] = useState('');
  const [discountCodes, setDiscountCodes] = useState([]);
  const [flyerSettings, setFlyerSettings] = useState({
    discount_code: '',
    discount_value: 10,
    discount_type: 'percentage',
    headline: 'Bestel online!',
    subtitle: 'Scan de QR code en krijg korting op je eerste bestelling',
    primary_color: '#1A4D2E',
    include_phone: true,
    include_address: true,
  });
  const [showCodeDialog, setShowCodeDialog] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  // Aparte QR mét kortingscode voor op de flyer, zodat scannen de korting
  // meteen toepast in plaats van dat de klant hem moet overtypen.
  const [flyerQrCode, setFlyerQrCode] = useState(null);
  const [flyerQrUrl, setFlyerQrUrl] = useState('');
  const [showFlyerPreview, setShowFlyerPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const flyerRef = useRef(null);

  const emptyCode = {
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    description: 'Korting op je eerste bestelling',
    min_order_amount: 0,
    max_uses: 0,
    first_order_only: true,
    active: true,
    valid_from: '',
    expires_at: '',
  };
  const [newCode, setNewCode] = useState(emptyCode);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [qrRes, codesRes, settingsRes] = await Promise.all([
        marketingAPI.getQRCode(),
        marketingAPI.getDiscountCodes(),
        marketingAPI.getFlyerSettings(),
      ]);
      setQrCode(qrRes.data.qr_code);
      setWebshopUrl(qrRes.data.webshop_url);
      setDiscountCodes(codesRes.data || []);
      if (settingsRes.data && Object.keys(settingsRes.data).length > 0) {
        setFlyerSettings(prev => ({ ...prev, ...settingsRes.data }));
      }
    } catch (error) {
      console.error('Error fetching marketing data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Zodra er een kortingscode voor de flyer gekozen is, een QR ophalen die
  // rechtstreeks naar de webshop wijst mét ?discount=<code>.
  useEffect(() => {
    const code = flyerSettings.discount_code;
    if (!code) {
      setFlyerQrCode(null);
      setFlyerQrUrl('');
      return;
    }
    let cancelled = false;
    marketingAPI
      .getQRCode(code)
      .then(({ data }) => {
        if (cancelled) return;
        setFlyerQrCode(data.qr_code);
        setFlyerQrUrl(data.webshop_url);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [flyerSettings.discount_code]);

  const downloadQRCode = () => {
    if (!qrCode) return;
    const link = document.createElement('a');
    link.download = `qr-code-${restaurant?.slug || 'webshop'}.png`;
    link.href = qrCode;
    link.click();
    toast.success('QR code gedownload!');
  };

  const copyWebshopUrl = () => {
    navigator.clipboard.writeText(webshopUrl);
    toast.success('Link gekopieerd!');
  };

  // Datums gaan als ISO naar de backend; de date-inputs werken met YYYY-MM-DD.
  const toPayload = (c) => ({
    ...c,
    valid_from: c.valid_from ? new Date(`${c.valid_from}T00:00:00`).toISOString() : null,
    expires_at: c.expires_at ? new Date(`${c.expires_at}T23:59:59`).toISOString() : null,
  });
  const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : '');

  const openCreateDialog = () => {
    setEditingCode(null);
    setNewCode(emptyCode);
    setShowCodeDialog(true);
  };

  const openEditDialog = (code) => {
    setEditingCode(code);
    setNewCode({
      code: code.code || '',
      discount_type: code.discount_type || 'percentage',
      discount_value: code.discount_value ?? 10,
      description: code.description || '',
      min_order_amount: code.min_order_amount ?? 0,
      max_uses: code.max_uses ?? 0,
      first_order_only: !!code.first_order_only,
      active: code.active !== false,
      valid_from: toDateInput(code.valid_from),
      expires_at: toDateInput(code.expires_at),
    });
    setShowCodeDialog(true);
  };

  const handleSaveCode = async () => {
    try {
      if (editingCode) {
        await marketingAPI.updateDiscountCode(editingCode.id, toPayload(newCode));
        // De update-endpoint geeft alleen een bevestiging terug, dus opnieuw ophalen
        // zodat de lijst exact toont wat er is opgeslagen.
        const { data } = await marketingAPI.getDiscountCodes();
        setDiscountCodes(data);
        toast.success('Kortingscode bijgewerkt!');
      } else {
        const response = await marketingAPI.createDiscountCode(toPayload(newCode));
        setDiscountCodes([response.data, ...discountCodes]);
        toast.success('Kortingscode aangemaakt!');
      }
      setShowCodeDialog(false);
      setEditingCode(null);
      setNewCode(emptyCode);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon code niet opslaan');
    }
  };

  const handleDeleteCode = async (codeId) => {
    if (!confirm('Weet je zeker dat je deze code wilt verwijderen?')) return;
    try {
      await marketingAPI.deleteDiscountCode(codeId);
      setDiscountCodes(discountCodes.filter(c => c.id !== codeId));
      toast.success('Kortingscode verwijderd');
    } catch (error) {
      toast.error('Kon code niet verwijderen');
    }
  };

  const handleSaveFlyerSettings = async () => {
    if (!flyerSettings.discount_code) {
      toast.error('Selecteer eerst een kortingscode');
      return;
    }
    setSaving(true);
    try {
      await marketingAPI.saveFlyerSettings(flyerSettings);
      toast.success('Flyer instellingen opgeslagen! Aankondiging is nu actief op je webshop.');
    } catch (error) {
      toast.error('Kon instellingen niet opslaan');
    } finally {
      setSaving(false);
    }
  };

  const printFlyer = () => {
    const node = flyerRef.current;
    if (!node) return;

    // De flyer is opgebouwd uit Tailwind-classes plus inline stijlen op het
    // hoofdelement (gradient, breedte, witte tekst). Een leeg popup-venster
    // heeft geen van beide, dus nemen we de stylesheets van de app mee én
    // gebruiken we outerHTML — innerHTML liet juist het hoofdelement weg,
    // waardoor achtergrond, kleur en breedte verdwenen.
    const styleLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      // el.href geeft de absolute URL; het popup-venster is about:blank, dus
      // een relatieve href zou daar nergens naar wijzen.
      .map((el) => `<link rel="stylesheet" href="${el.href}">`)
      .join('\n');
    const inlineStyles = Array.from(document.querySelectorAll('style'))
      .map((el) => el.outerHTML)
      .join('\n');

    const printWindow = window.open('', '_blank', 'width=900,height=1200');
    if (!printWindow) {
      toast.error('Pop-up geblokkeerd — sta pop-ups toe om de flyer af te drukken');
      return;
    }

    printWindow.document.write(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <base href="${document.baseURI}" />
    <title>Flyer - ${restaurant?.name || 'FoodFlows'}</title>
    ${styleLinks}
    ${inlineStyles}
    <style>
      @page { size: A4 portrait; margin: 12mm; }
      html, body { margin: 0; padding: 0; background: #fff; }
      body { display: flex; justify-content: center; align-items: flex-start; }
      /* Zonder dit laten browsers achtergrondkleuren en de gradient weg bij
         het printen, waardoor de flyer zwart-op-wit uit de printer komt. */
      *, *::before, *::after {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      @media print {
        .flyer-print-root > * { box-shadow: none !important; margin: 0 !important; }
      }
    </style>
  </head>
  <body><div class="flyer-print-root">${node.outerHTML}</div></body>
</html>`);
    printWindow.document.close();

    // Pas afdrukken zodra stylesheets en afbeeldingen (logo, QR) geladen zijn.
    // De oude versie riep print() direct na document.close() aan, waardoor de
    // browser een nog niet opgemaakte pagina naar de printer stuurde.
    let printed = false;
    const startPrint = () => {
      if (printed) return;
      printed = true;
      printWindow.focus();
      printWindow.print();
    };
    printWindow.addEventListener('load', () => setTimeout(startPrint, 250));
    // Vangnet voor het geval het load-event al gepasseerd is of een externe
    // afbeelding blijft hangen.
    setTimeout(startPrint, 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="marketing-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Marketing & QR Codes</h1>
        <p className="text-gray-600 mt-1">Genereer QR codes, kortingscodes en flyers voor je restaurant</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* QR Code Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <QrCode className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Webshop QR Code</h2>
              <p className="text-sm text-gray-500">Laat klanten direct naar je webshop scannen</p>
            </div>
          </div>

          <div className="flex flex-col items-center">
            {qrCode && (
              <div className="bg-white p-4 rounded-xl border-2 border-gray-100 mb-4">
                <img src={qrCode} alt="QR Code" className="w-48 h-48" />
              </div>
            )}
            
            <p className="text-sm text-gray-600 text-center mb-4 font-mono bg-gray-50 px-4 py-2 rounded-lg">
              {webshopUrl}
            </p>

            <div className="flex gap-2">
              <Button variant="outline" onClick={copyWebshopUrl}>
                <Copy className="w-4 h-4 mr-2" />
                Kopieer Link
              </Button>
              <Button className="btn-primary" onClick={downloadQRCode}>
                <Download className="w-4 h-4 mr-2" />
                Download QR
              </Button>
            </div>
          </div>
        </div>

        {/* Discount Codes Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Tag className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">Kortingscodes</h2>
                <p className="text-sm text-gray-500">Beheer kortingscodes voor je webshop</p>
              </div>
            </div>
            <Button 
              onClick={openCreateDialog}
              className="btn-primary"
              data-testid="create-discount-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nieuwe Code
            </Button>
          </div>

          {discountCodes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Tag className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Nog geen kortingscodes</p>
              <p className="text-sm">Maak je eerste kortingscode aan</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {discountCodes.map((code) => (
                <div 
                  key={code.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    code.active ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      code.active ? 'bg-emerald-100' : 'bg-gray-200'
                    }`}>
                      {code.discount_type === 'percentage' ? (
                        <Percent className={`w-4 h-4 ${code.active ? 'text-emerald-600' : 'text-gray-400'}`} />
                      ) : (
                        <Euro className={`w-4 h-4 ${code.active ? 'text-emerald-600' : 'text-gray-400'}`} />
                      )}
                    </div>
                    <div>
                      <p className="font-mono font-semibold text-gray-900">{code.code}</p>
                      <p className="text-sm text-gray-500">
                        {code.discount_type === 'percentage' 
                          ? `${code.discount_value}% korting` 
                          : `€${code.discount_value.toFixed(2)} korting`}
                        {code.first_order_only && ' • Eerste bestelling'}
                      </p>
                      {(code.valid_from || code.expires_at) && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {code.valid_from ? `vanaf ${toDateInput(code.valid_from)}` : 'direct geldig'}
                          {code.expires_at ? ` t/m ${toDateInput(code.expires_at)}` : ' • geen einddatum'}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded ${
                      code.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {code.active ? 'Actief' : 'Inactief'}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(code)}
                      data-testid={`edit-code-${code.id}`}
                      title="Bewerken"
                    >
                      <Edit2 className="w-4 h-4 text-gray-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteCode(code.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Flyer Generator Section */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">Flyer Generator</h2>
            <p className="text-sm text-gray-600">Maak een printbare flyer met QR code en kortingscode</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Settings */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Kortingscode voor flyer</Label>
              <select 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={flyerSettings.discount_code}
                onChange={(e) => setFlyerSettings({...flyerSettings, discount_code: e.target.value})}
              >
                <option value="">Selecteer een code...</option>
                {discountCodes.filter(c => c.active).map(code => (
                  <option key={code.id} value={code.code}>
                    {code.code} ({code.discount_type === 'percentage' ? `${code.discount_value}%` : `€${code.discount_value}`})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label>Titel</Label>
              <Input 
                value={flyerSettings.headline}
                onChange={(e) => setFlyerSettings({...flyerSettings, headline: e.target.value})}
                placeholder="Bestel online!"
              />
            </div>

            <div className="space-y-2">
              <Label>Ondertitel</Label>
              <Textarea 
                value={flyerSettings.subtitle}
                onChange={(e) => setFlyerSettings({...flyerSettings, subtitle: e.target.value})}
                placeholder="Scan de QR code..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Hoofdkleur</Label>
              <div className="flex items-center gap-2">
                <input 
                  type="color" 
                  value={flyerSettings.primary_color}
                  onChange={(e) => setFlyerSettings({...flyerSettings, primary_color: e.target.value})}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
                <Input 
                  value={flyerSettings.primary_color}
                  onChange={(e) => setFlyerSettings({...flyerSettings, primary_color: e.target.value})}
                  className="flex-1"
                />
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-700">Telefoonnummer tonen</span>
              <Switch 
                checked={flyerSettings.include_phone}
                onCheckedChange={(checked) => setFlyerSettings({...flyerSettings, include_phone: checked})}
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-700">Adres tonen</span>
              <Switch 
                checked={flyerSettings.include_address}
                onCheckedChange={(checked) => setFlyerSettings({...flyerSettings, include_address: checked})}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowFlyerPreview(true)}
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button 
                className="btn-primary flex-1"
                onClick={handleSaveFlyerSettings}
                disabled={saving || !flyerSettings.discount_code}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Megaphone className="w-4 h-4 mr-2" />
                )}
                Opslaan & Activeer Aankondiging
              </Button>
            </div>
          </div>

          {/* Mini Preview */}
          <div className="bg-white rounded-xl p-4 border border-amber-200">
            <p className="text-xs text-gray-500 mb-2">Flyer Preview (verkleind)</p>
            <div 
              className="rounded-lg overflow-hidden shadow-lg"
              style={{ 
                background: `linear-gradient(135deg, ${flyerSettings.primary_color} 0%, ${flyerSettings.primary_color}dd 100%)`,
                aspectRatio: '1/1.4',
                padding: '1rem',
                color: 'white'
              }}
            >
              <div className="text-center h-full flex flex-col justify-between">
                <div>
                  <p className="font-bold text-sm">{restaurant?.name}</p>
                  <p className="text-xl font-bold mt-2">{flyerSettings.headline}</p>
                  <p className="text-xs opacity-90 mt-1">{flyerSettings.subtitle}</p>
                </div>
                <div className="bg-white rounded-lg p-2 mx-auto">
                  {(flyerQrCode || qrCode) && <img src={flyerQrCode || qrCode} alt="QR" className="w-16 h-16" />}
                </div>
                <div className="bg-white/20 rounded-lg p-2">
                  <p className="text-xs">Gebruik code:</p>
                  <p className="font-bold text-sm">{flyerSettings.discount_code || 'CODE'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Webshop Announcement Preview */}
      {flyerSettings.discount_code && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Megaphone className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold text-blue-900">Aankondiging op je Webshop</h3>
              <p className="text-sm text-blue-700">Dit is hoe klanten de aankondiging zien</p>
            </div>
          </div>
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-4 py-2 rounded-lg text-center">
            <p className="text-sm">
              <Sparkles className="w-4 h-4 inline mr-1" />
              <strong>{discountCodes.find(c => c.code === flyerSettings.discount_code)?.discount_type === 'percentage' 
                ? `${discountCodes.find(c => c.code === flyerSettings.discount_code)?.discount_value}%` 
                : `€${discountCodes.find(c => c.code === flyerSettings.discount_code)?.discount_value}`} KORTING</strong> op je eerste bestelling! 
              Gebruik code: <span className="font-mono bg-white/20 px-2 py-0.5 rounded">{flyerSettings.discount_code}</span>
            </p>
          </div>
        </div>
      )}

      {/* Create Discount Code Dialog */}
      <Dialog open={showCodeDialog} onOpenChange={setShowCodeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCode ? 'Kortingscode Bewerken' : 'Nieuwe Kortingscode'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Code (laat leeg voor automatisch)</Label>
              <Input 
                value={newCode.code}
                onChange={(e) => setNewCode({...newCode, code: e.target.value.toUpperCase()})}
                placeholder="bijv. WELKOM10"
                className="font-mono"
                maxLength={12}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type korting</Label>
                <select 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={newCode.discount_type}
                  onChange={(e) => setNewCode({...newCode, discount_type: e.target.value})}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Vast bedrag (€)</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Waarde</Label>
                <Input 
                  type="number"
                  value={newCode.discount_value}
                  onChange={(e) => setNewCode({...newCode, discount_value: parseFloat(e.target.value) || 0})}
                  min={0}
                  step={newCode.discount_type === 'percentage' ? 1 : 0.5}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Beschrijving</Label>
              <Input 
                value={newCode.description}
                onChange={(e) => setNewCode({...newCode, description: e.target.value})}
                placeholder="Korting op je eerste bestelling"
              />
            </div>

            <div className="space-y-2">
              <Label>Minimum bestelwaarde (€)</Label>
              <Input 
                type="number"
                value={newCode.min_order_amount}
                onChange={(e) => setNewCode({...newCode, min_order_amount: parseFloat(e.target.value) || 0})}
                min={0}
                step={1}
              />
            </div>

            <div className="space-y-2">
              <Label>Maximum aantal keer te gebruiken (0 = onbeperkt)</Label>
              <Input 
                type="number"
                value={newCode.max_uses}
                onChange={(e) => setNewCode({...newCode, max_uses: parseInt(e.target.value) || 0})}
                min={0}
              />
            </div>

            <div className="flex items-center justify-between py-2 bg-gray-50 px-3 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Alleen eerste bestelling</p>
                <p className="text-sm text-gray-500">Code werkt alleen voor nieuwe klanten</p>
              </div>
              <Switch
                checked={newCode.first_order_only}
                onCheckedChange={(checked) => setNewCode({...newCode, first_order_only: checked})}
              />
            </div>

            {newCode.first_order_only && (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                Deze korting wordt <strong>automatisch toegepast</strong> zodra een klant die nog nooit
                bij jou besteld heeft zijn e-mailadres of telefoonnummer invult. Hij hoeft de code
                dus niet zelf in te typen.
              </p>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Geldig vanaf</Label>
                <Input
                  type="date"
                  value={newCode.valid_from}
                  onChange={(e) => setNewCode({ ...newCode, valid_from: e.target.value })}
                  data-testid="code-valid-from"
                />
                <p className="text-xs text-gray-500">Leeg = direct geldig</p>
              </div>
              <div className="space-y-2">
                <Label>Geldig t/m</Label>
                <Input
                  type="date"
                  value={newCode.expires_at}
                  onChange={(e) => setNewCode({ ...newCode, expires_at: e.target.value })}
                  data-testid="code-expires-at"
                />
                <p className="text-xs text-gray-500">Leeg = geen einddatum</p>
              </div>
            </div>

            <div className="flex items-center justify-between py-2 bg-gray-50 px-3 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Actief</p>
                <p className="text-sm text-gray-500">Uitgeschakelde codes werken niet meer</p>
              </div>
              <Switch
                checked={newCode.active}
                onCheckedChange={(checked) => setNewCode({ ...newCode, active: checked })}
                data-testid="code-active"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCodeDialog(false)}>
              Annuleren
            </Button>
            <Button className="btn-primary" onClick={handleSaveCode} data-testid="save-code-btn">
              {editingCode ? (
                <><Check className="w-4 h-4 mr-2" />Opslaan</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" />Aanmaken</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Flyer Preview Dialog */}
      <Dialog open={showFlyerPreview} onOpenChange={setShowFlyerPreview}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Flyer Preview</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div
              ref={flyerRef}
              className="mx-auto shadow-xl"
              style={{
                width: '480px',
                minHeight: '680px',
                background: `linear-gradient(135deg, ${flyerSettings.primary_color} 0%, ${flyerSettings.primary_color}dd 100%)`,
                padding: '2.5rem',
                color: 'white',
                borderRadius: '12px'
              }}
            >
              <div className="text-center flex flex-col" style={{ minHeight: '600px', gap: '1.25rem' }}>
                {/* Restaurant logo (if available) + name */}
                <div className="flex flex-col items-center">
                  {restaurant?.logo_url && (
                    <div className="bg-white rounded-2xl p-3 shadow-lg mb-3" data-testid="flyer-logo-wrapper">
                      <img
                        src={restaurant.logo_url}
                        alt={`${restaurant?.name} logo`}
                        className="h-16 w-auto object-contain"
                        data-testid="flyer-logo"
                      />
                    </div>
                  )}
                  <h1 className="text-2xl font-bold">{restaurant?.name}</h1>
                </div>

                <div>
                  <h2 className="text-3xl font-bold">{flyerSettings.headline}</h2>
                  <p className="text-base opacity-90 mt-2">{flyerSettings.subtitle}</p>
                </div>

                {/* BIG QR Code so it's super-readable when printed */}
                <div className="bg-white rounded-2xl p-5 shadow-lg mx-auto" data-testid="flyer-qr-wrapper">
                  {(flyerQrCode || qrCode) && (
                    <img
                      src={flyerQrCode || qrCode}
                      alt="QR Code"
                      className="mx-auto"
                      style={{ width: '320px', height: '320px', imageRendering: 'pixelated' }}
                      data-testid="flyer-qr"
                    />
                  )}
                  <p className="text-gray-700 text-sm mt-3 font-medium">Scan met je telefoon-camera</p>
                  {(flyerQrUrl || webshopUrl) && (
                    <p
                      className="text-gray-500 text-xs mt-1 break-all font-mono"
                      data-testid="flyer-webshop-url"
                    >
                      {(flyerQrUrl || webshopUrl).replace(/^https?:\/\//, '')}
                    </p>
                  )}
                </div>

                <div className="bg-white/20 backdrop-blur rounded-xl p-4">
                  <p className="text-white/80 text-sm">Gebruik kortingscode:</p>
                  <p className="text-3xl font-bold font-mono mt-1">{flyerSettings.discount_code || 'CODE'}</p>
                  <p className="text-white/90 text-sm mt-2">
                    {discountCodes.find(c => c.code === flyerSettings.discount_code)?.discount_type === 'percentage'
                      ? `${discountCodes.find(c => c.code === flyerSettings.discount_code)?.discount_value}% korting`
                      : `€${discountCodes.find(c => c.code === flyerSettings.discount_code)?.discount_value} korting`}
                    {' '}op je eerste bestelling!
                  </p>
                </div>

                <div className="text-white/85 text-sm space-y-1 mt-auto">
                  {flyerSettings.include_phone && restaurant?.phone && (
                    <p><Phone className="w-4 h-4 inline mr-1" />{restaurant.phone}</p>
                  )}
                  {flyerSettings.include_address && restaurant?.address && (
                    <p><MapPin className="w-4 h-4 inline mr-1" />{restaurant.address}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFlyerPreview(false)}>
              Sluiten
            </Button>
            <Button variant="outline" onClick={downloadQRCode}>
              <Download className="w-4 h-4 mr-2" />
              Download QR
            </Button>
            <Button className="btn-primary" onClick={printFlyer}>
              <Printer className="w-4 h-4 mr-2" />
              Afdrukken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
